import {
  formatDate as _formatDate,
  today as _today,
  isValidDate,
  minValue,
  parseDate,
  parseDateExpression,
  parseISODate,
  toDateTimeTypeString,
} from "DateExtensions";
import { DateTimeType } from "DateTimeConstants";
import { type TimeInfo } from "LocaleSettings";
import { type DateFormatter as wtgMaterialDateFormatter } from "wtg-material-ui";

/*! SuppressStringValidation Type value */
const dateFormat = "YYYY-MM-DD";

export default class DateFormatter implements wtgMaterialDateFormatter {
  readonly timeInfo: TimeInfo;

  constructor(timeInfo: TimeInfo) {
    this.timeInfo = timeInfo;
  }

  private getTimeZone(): string {
    return this.timeInfo.defaultTimeZone ?? "";
  }

  format(isoDate: string): string {
    if (isoDate) {
      const parsed = parseISODate(isoDate, DateTimeType.Date, this.getTimeZone());
      if (parsed.isValid()) {
        const minMoment = minValue();
        const result =
          !minMoment.isAfter(parsed) && !minMoment.isSame(parsed)
            ? toDateTimeTypeString(parsed, DateTimeType.Date)
            : "";
        return result;
      }
    }
    return isoDate;
  }

  parse(date: string): string | null {
    if (date === "") {
      return "";
    }

    let result = null;
    if (date) {
      const value = date;
      let momentValue = parseDateExpression(
        value,
        DateTimeType.Date,
        this.getTimeZone(),
        this.timeInfo.dateTimeFormatConfig,
      );
      if (!momentValue.isValid()) {
        momentValue = parseDate(value, DateTimeType.Date, this.getTimeZone(), this.timeInfo.dateTimeFormatConfig);
      }

      if (momentValue.isValid()) {
        result = _formatDate(momentValue, dateFormat);
      }
    }
    return result;
  }

  today(): string {
    return _formatDate(_today(this.getTimeZone()), dateFormat);
  }

  isValid(isoDate: string): boolean {
    const momentValue = parseDate(isoDate, DateTimeType.Date);
    return isValidDate(momentValue.toDate());
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isValidMonthYear(isoMonthDate: string): boolean {
    throw new Error("Setting monthYearOnly is not supported");
  }
}

export function formatDate(date: Date): string {
  return _formatDate(date, dateFormat);
}
